import {Component, computed, DestroyRef, inject, resource, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {take} from 'rxjs/operators';
import {FluxCard, FluxDirection, FluxStatus, LanceursFluxService, ProviderSection} from '../../../core/services/gef/lanceurs-flux.service';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';
import {PROVIDER_CODE_DOL, PROVIDER_CODE_ELAP} from './lanceurs-flux-gef.constants';

const ELAP_FLUX_LIST: FluxCard[] = [
  {id: 'export-fournisseurs',           label: 'Fournisseurs',           direction: 'EXPORT', status: 'idle'},
  {id: 'export-articles',               label: 'Articles',               direction: 'EXPORT', status: 'idle'},
  {id: 'export-commandes-fournisseurs', label: 'Commandes fournisseurs', direction: 'EXPORT', status: 'idle'},
  {id: 'export-bons-reception',         label: 'Bons de réception',      direction: 'EXPORT', status: 'idle'},
];

@Component({
  selector: 'yo-lanceurs-flux-gef',
  templateUrl: './lanceurs-flux-gef.component.html',
  styleUrls: ['./lanceurs-flux-gef.component.scss'],
  standalone: false
})
export class LanceursFluxGefComponent {

  protected readonly ButtonThemeEnum     = ButtonThemeEnum;
  protected readonly lanceursSvc         = inject(LanceursFluxService);
  protected readonly configDialogVisible = signal(false);

  private readonly destroyRef = inject(DestroyRef);

  private readonly dolFluxResource = resource<FluxCard[], void>({
    loader: async () => this.lanceursSvc.loadDolApiWebhooks().pipe(take(1)).toPromise().then(r => r ?? [])
  });

  private readonly fluxStatuses = signal<Record<string, FluxStatus>>({});

  protected readonly directionList: FluxDirection[] = ['IMPORT', 'EXPORT'];

  protected readonly providerList = computed<ProviderSection[]>(() => {
    const statuses    = this.fluxStatuses();
    const dolFluxList = (this.dolFluxResource.value() ?? [])
                          .map(f => ({...f, status: statuses[f.id] ?? 'idle' as FluxStatus}));
    return [
      {
        providerCode: PROVIDER_CODE_ELAP,
        label: 'ELAP',
        description: 'Échange via API REST',
        mode: 'API',
        available: true,
        loading: false,
        fluxList: ELAP_FLUX_LIST.map(f => ({...f, status: statuses[f.id] ?? 'idle' as FluxStatus}))
      },
      {
        providerCode: PROVIDER_CODE_DOL,
        label: 'Flux disponibles',
        description: 'Déclenchement via webhook (outil tiers d\'orchestration)',
        mode: 'Webhook',
        loading: this.dolFluxResource.isLoading(),
        available: dolFluxList.length > 0 && !this.dolFluxResource.isLoading(),
        fluxList: dolFluxList
      }
    ];
  });

  launch(provider: ProviderSection, flux: FluxCard): void {
    if (!provider.available || flux.status === 'running') { return; }
    this.setFluxStatus(flux.id, 'running');

    if (provider.providerCode === PROVIDER_CODE_ELAP) {
      const obs$ = this.lanceursSvc.launchElapApi(flux);
      if (!obs$) { this.setFluxStatus(flux.id, 'idle'); return; }
      obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next:  (r) => this.setFluxStatus(flux.id, this.lanceursSvc.notifyResult(r, 'Batch terminé', 'Batch terminé en erreur')),
        error: ()  => this.setFluxStatus(flux.id, this.lanceursSvc.notifyError('Erreur lors du lancement du flux'))
      });
    } else if (provider.providerCode === PROVIDER_CODE_DOL) {
      this.lanceursSvc.launchDolApi(flux).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next:  (r) => this.setFluxStatus(flux.id, this.lanceursSvc.notifyResult(r, 'Webhook déclenché avec succès', 'Erreur lors du déclenchement du webhook')),
        error: ()  => this.setFluxStatus(flux.id, this.lanceursSvc.notifyError('Erreur lors du déclenchement du webhook'))
      });
    }
  }

  getFluxByDirection(provider: ProviderSection, direction: FluxDirection): FluxCard[] {
    return provider.fluxList.filter(f => f.direction === direction);
  }

  hasDirection(provider: ProviderSection, direction: string): boolean {
    return provider.fluxList.some(f => f.direction === direction);
  }

  hasAnyDirection(provider: ProviderSection): boolean {
    return provider.fluxList.some(f => f.direction != null);
  }

  private setFluxStatus(fluxId: string, status: FluxStatus): void {
    this.fluxStatuses.update(m => ({...m, [fluxId]: status}));
  }
}
